import React, { useRef } from "react";
import { MusicHallBookingFormStyled } from "./styled";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import MusicHallForm from "./MusicHallForm";
import MusicPracticeForm from "./MusicPracticeForm";
import { BOOKING_SOURCE } from "utils/functions/musicHall";
import { IS_PRACTICE_ROOM_CLOSED_DAY } from "utils/functions/roomBookingTime";
import { Buttons, Inputs } from "components";

const MusicHallBookingForm = ({
  initialValues,
  permission,
  equipmentAdditionalList,
  equipmentProviderList,
  musicHallRoomList,
  practiceRoomList,
  onCancel,
  onDelete,
  onSubmit,
}) => {
  const schema = yup.object().shape({
    room_type: yup.string().required("required"),
    room_ref_id: yup.string().required("required"),
    booking_source: yup.string().required("required"),
    booker_first_name: yup.string().required("required"),
    booker_last_name: yup.string().required("required"),
    booker_phone: yup.string().required("required"),
    booker_email: yup.string().email("Invalid email").required("required"),
    event_start_time: yup.string().required("required"),
    event_end_time: yup.string().required("required"),
    // ด้านล่างให้ตรงกับฝั่งลูกค้า: ต้องเลือกวัน / Music Hall ต้องมีเวลาเตรียม-เก็บงาน / ห้องซ้อมปิดวันจันทร์
    booking_date: yup
      .mixed()
      .test("required", "required", (value) => !!value)
      .when("room_type", {
        is: "2",
        then: (s) =>
          s.test(
            "closed_day",
            "ห้องซ้อมดนตรีปิดทุกวันจันทร์",
            (value) => !IS_PRACTICE_ROOM_CLOSED_DAY(value),
          ),
      }),
    setup_cleanup_start_time: yup.string().when("room_type", {
      is: "1",
      then: (s) => s.required("required"),
    }),
    setup_cleanup_end_time: yup.string().when("room_type", {
      is: "1",
      then: (s) => s.required("required"),
    }),
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { ...initialValues },
  });

  // เปลี่ยน Room Type แล้วล้างข้อมูลการจองที่กรอกไว้ทั้งหมด กันค่าของอีกประเภทค้าง
  // (เช่น room_ref_id ของ Music Hall ค้างใน dropdown ห้องซ้อม โชว์เป็น id / เวลาเตรียมงานติดไปห้องซ้อม)
  // ไม่ล้าง: ชื่อ นามสกุล เบอร์ อีเมล (ข้อมูลผู้จอง ใช้ได้ทั้งสองประเภท) และ Type (Customer/Internal)
  // Dropdown เรียก onChange (ตั้งค่าใหม่) ก่อน onChaneCustom จึงต้องจำค่าเดิมไว้เอง
  const _prevRoomTypeRef = useRef(initialValues?.room_type || "");
  const _handleChangeRoomType = (roomType) => {
    const prevRoomType = _prevRoomTypeRef.current;
    _prevRoomTypeRef.current = roomType;
    // เลือกครั้งแรก (ยังไม่มีอะไรให้ล้าง) หรือเลือกค่าเดิมซ้ำ ไม่ต้องล้าง
    if (!prevRoomType || prevRoomType === roomType) return;
    Object.entries(ROOM_TYPE_RESET_VALUES).forEach(([name, value]) =>
      setValue(name, value),
    );
    clearErrors(Object.keys(ROOM_TYPE_RESET_VALUES));
  };

  const watchRoomType = watch("room_type");
  const watchBookingSource = watch("booking_source");
  const eventStartTime = watch("event_start_time");
  const eventEndTime = watch("event_end_time");
  const setupStartTime = watch("setup_cleanup_start_time");
  const setupEndTime = watch("setup_cleanup_end_time");

  const formDisabled = initialValues;
  return (
    <MusicHallBookingFormStyled>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="input_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.Dropdown
                {...field}
                label={"Room Type"}
                disabled={formDisabled}
                onChaneCustom={_handleChangeRoomType}
                options={[
                  { label: "Music Hall", value: "1" },
                  { label: "Music Practice Room", value: "2" },
                ]}
                // placeholder={t('test')}
                errors={errors.room_type?.message}
              />
            )}
            name="room_type"
            defaultValue=""
          />
        </div>
        <div className="input_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.Dropdown
                {...field}
                label={"Type"}
                options={BOOKING_SOURCE}
                // placeholder={t('test')}
                errors={errors.booking_source?.message}
              />
            )}
            name="booking_source"
            defaultValue=""
          />
          {/* ตอนสร้างใหม่เท่านั้น (API insert) แก้ Type ทีหลังไม่เปลี่ยนสถานะ/ราคาให้ */}
          {!initialValues && watchBookingSource === "2" && (
            <div className="field_hint">
              Internal: ยืนยันการจองทันที ไม่ต้องโอน/อัปสลิป
              ส่งอีเมลยืนยันการจอง (Booking Confirmed) และยอดรับ/รายได้ในบัญชีเป็น 0
            </div>
          )}
        </div>
        {watchRoomType && (
          <>
            {watchRoomType === "1" && (
              <MusicHallForm
                control={control}
                errors={errors}
                equipmentAdditionalList={equipmentAdditionalList}
                equipmentProviderList={equipmentProviderList}
                musicHallRoomList={musicHallRoomList}
                setupStartTime={setupStartTime}
                setupEndTime={setupEndTime}
                eventStartTime={eventStartTime}
                eventEndTime={eventEndTime}
                watch={watch}
                setValue={setValue}
              />
            )}
            {watchRoomType === "2" && (
              <MusicPracticeForm
                control={control}
                errors={errors}
                practiceRoomList={practiceRoomList}
                watch={watch}
                eventStartTime={eventStartTime}
                eventEndTime={eventEndTime}
                setValue={setValue}
              />
            )}
          </>
        )}
        <div className="input_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.TextArea
                {...field}
                label={"Admin โน้ตรายละเอียดในงาน"}
                // placeholder={t('test')}
                errors={errors.event_admin_note?.message}
              />
            )}
            name="event_admin_note"
            defaultValue=""
          />
        </div>
        <div className="btn_zone">
          <div className="w120">
            <Buttons.BgStandard theme_gray label="Back" onClick={onCancel} />
          </div>
          {permission && (
            <>
              {initialValues && permission.is_delete && (
                <div className="w120">
                  <Buttons.BgStandard
                    theme_red
                    label="Delete"
                    onClick={onDelete}
                    disabled={!initialValues.is_edit}
                  />
                </div>
              )}
              {initialValues && permission.is_update ? (
                <div className="w120">
                  <Buttons.BgStandard
                    type="submit"
                    label="Update"
                    // disabled={initialValues && initialValues.is_center}
                  />
                </div>
              ) : (
                permission.is_create && (
                  <div className="w120">
                    <Buttons.BgStandard
                      type="submit"
                      label="Save"
                      // disabled={initialValues && initialValues.is_center}
                    />
                  </div>
                )
              )}
            </>
          )}
        </div>
      </form>
    </MusicHallBookingFormStyled>
  );
};

// ช่องที่ล้างตอนเปลี่ยน Room Type (ค่าว่างแบบเดียวกับ defaultValue ของแต่ละช่อง)
const ROOM_TYPE_RESET_VALUES = {
  room_ref_id: "",
  booking_date: "",
  setup_cleanup_start_time: "",
  setup_cleanup_end_time: "",
  event_start_time: "",
  event_end_time: "",
  event_name: "",
  event_type: "",
  expected_attendees: "",
  additional_equipment_provider: [],
  additional_equipment_additional: [],
  special_request: "",
  event_admin_note: "",
};

MusicHallBookingForm.propTypes = {};

export default MusicHallBookingForm;
